import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from '../../lib/axios'
import {
  Plus,
  Trash2,
  Eye,
  ShieldCheck,
  RefreshCw,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Calendar
} from 'lucide-react'
import '../Pages.css'

export default function PlacementTests() {
  const [tests, setTests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)

  useEffect(() => {
    fetchTests()
  }, [])

  const fetchTests = () => {
    setLoading(true)
    axios
      .get('api/placement/tests')
      .then((res) => {
        const data = res.data
        setTests(Array.isArray(data) ? data : data.tests || data.data || [])
        setError(null)
      })
      .catch((err) => {
        console.error(err)
        setError('Failed to load placement tests')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleDelete = async (testId) => {
    if (!confirm('Are you sure you want to delete this placement test?')) return
    setActionLoading(testId)
    try {
      await axios.delete(`api/placement/tests/${testId}`)
      fetchTests()
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete test')
    } finally {
      setActionLoading(null)
    }
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', bg: 'rgba(100, 116, 139, 0.1)', color: '#64748b', icon: Clock },
      sent: { label: 'Sent', bg: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', icon: Mail },
      checking: { label: 'Checking', bg: 'rgba(234, 179, 8, 0.1)', color: '#eab308', icon: RefreshCw },
      completed: { label: 'Completed', bg: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', icon: CheckCircle },
      failed: { label: 'Failed', bg: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', icon: XCircle }
    }
    const config = statusConfig[status] || statusConfig.pending
    const Icon = config.icon
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
        padding: '0.375rem 0.75rem', background: config.bg, color: config.color,
        borderRadius: 'var(--radius-full)', fontSize: '0.75rem', fontWeight: '600',
        textTransform: 'uppercase', letterSpacing: '0.025em'
      }}>
        <Icon size={12} />
        {config.label}
      </span>
    )
  }

  const getInboxRateColor = (rate) => {
    if (rate >= 80) return '#22c55e'
    if (rate >= 50) return '#eab308'
    return '#ef4444'
  }

  const getInboxRateBg = (rate) => {
    if (rate >= 80) return 'rgba(34, 197, 94, 0.1)'
    if (rate >= 50) return 'rgba(234, 179, 8, 0.1)'
    return 'rgba(239, 68, 68, 0.1)'
  }

  return (
    <div className="page animate-fade-in">
      <div className="page-header">
        <div className="header-info">
          <h2>Inbox Placement Tests</h2>
          <p>Test where your emails land before sending to your entire contact list</p>
        </div>
        <Link to="/inbox-placement/new" className="btn btn-primary" style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
        }}>
          <Plus size={18} />
          <span>New Test</span>
        </Link>
      </div>

      {error && (
        <div className="card" style={{
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <AlertCircle size={20} style={{ color: '#ef4444' }} />
            <span style={{ color: '#dc2626', flex: 1 }}>{error}</span>
            <button onClick={fetchTests} className="btn btn-primary" style={{ padding: '0.5rem 1rem' }}>
              <RefreshCw size={16} />
              <span>Retry</span>
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div className="loader"></div>
        </div>
      ) : tests.length === 0 ? (
        <div className="card" style={{
          maxWidth: '700px', margin: '3rem auto',
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.03) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.1)'
        }}>
          <div style={{ padding: '5rem 2rem', textAlign: 'center' }}>
            <div style={{
              width: '120px', height: '120px', margin: '0 auto 2rem',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '50%', boxShadow: '0 20px 40px rgba(99, 102, 241, 0.3)'
            }}>
              <ShieldCheck size={56} style={{ color: 'white' }} />
            </div>
            <h3 style={{
              marginBottom: '0.75rem', fontSize: '1.75rem', fontWeight: '700',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              No Placement Tests Yet
            </h3>
            <p style={{
              color: 'var(--color-text-secondary)', marginBottom: '2.5rem',
              fontSize: '1rem', lineHeight: '1.6', maxWidth: '500px', margin: '0 auto 2.5rem'
            }}>
              Run a placement test to check if your emails land in Inbox or Spam before sending to your entire contact list.
            </p>
            <Link to="/inbox-placement/new" className="btn btn-primary" style={{
              padding: '0.875rem 2rem', fontSize: '1rem',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
            }}>
              <Plus size={20} />
              <span>Run Your First Test</span>
            </Link>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {tests.map((test) => (
            <div key={test.id} className="card" style={{ transition: 'all 0.2s', overflow: 'hidden' }}>
              <div style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                {/* Icon */}
                <div style={{
                  width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: test.status === 'completed'
                    ? `linear-gradient(135deg, ${getInboxRateColor(test.inbox_rate || 0)} 0%, ${getInboxRateColor(test.inbox_rate || 0)}cc 100%)`
                    : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  borderRadius: 'var(--radius-lg)', flexShrink: 0
                }}>
                  <ShieldCheck size={28} style={{ color: 'white' }} />
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <h3 style={{
                      fontSize: '1.1rem', fontWeight: '600', margin: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {test.subject || 'Untitled Test'}
                    </h3>
                    {getStatusBadge(test.status)}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '1.25rem',
                    fontSize: '0.8rem', color: 'var(--color-text-muted)'
                  }}>
                    {test.created_at && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <Calendar size={13} />
                        {new Date(test.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Inbox Rate */}
                {test.status === 'completed' && test.inbox_rate != null && (
                  <div style={{
                    padding: '0.5rem 1rem', borderRadius: 'var(--radius-md)',
                    background: getInboxRateBg(test.inbox_rate), textAlign: 'center', flexShrink: 0
                  }}>
                    <div style={{
                      fontSize: '1.5rem', fontWeight: '700', color: getInboxRateColor(test.inbox_rate)
                    }}>
                      {Math.round(test.inbox_rate)}%
                    </div>
                    <div style={{ fontSize: '0.7rem', color: getInboxRateColor(test.inbox_rate), fontWeight: '500' }}>
                      Inbox Rate
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Link
                    to={`/inbox-placement/${test.id}`}
                    className="btn btn-sm"
                    style={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      color: 'white', border: 'none'
                    }}
                  >
                    <Eye size={14} />
                    <span>View</span>
                  </Link>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => handleDelete(test.id)}
                    disabled={actionLoading === test.id}
                    style={{ color: '#ef4444' }}
                  >
                    {actionLoading === test.id ? <RefreshCw size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
